// ComponentInitializerは各実装クラスでインポートするため、ここでは不要

/**
 * ファイルアップローダーの設定インターフェース
 */
export interface FileUploaderConfig {
  fileInputName: string;
  nonceName: string;
  uploadAction: string;
  deleteAction: string;
  acceptedFileTypes?: string;
  maxFileSize?: number;
  messages?: {
    uploading?: string;
    uploadSuccess?: string;
    deleting?: string;
    deleteSuccess?: string;
    deleteConfirm?: string;
    communicationError?: string;
  };
}

/**
 * ファイルアップロード機能の基底クラス
 */
export abstract class FileUploader {
  protected container: HTMLElement;
  protected fileInput: HTMLInputElement | null;
  protected nonceField: HTMLInputElement | null;
  protected ajaxUrl: string;
  protected config: FileUploaderConfig;
  // バインドされた関数の参照を保存
  private boundHandleFileChange: (event: Event) => void;
  private boundHandleFileUploadButtonClick: (event: Event) => void;
  private boundHandleDelete: (event: Event) => void;

  constructor(element: HTMLElement, config: FileUploaderConfig) {
    this.container = element;
    this.config = this.mergeConfig(config);
    this.fileInput = this.container.querySelector(`input[name="${this.config.fileInputName}"]`);
    this.nonceField = document.querySelector(`input[name="${this.config.nonceName}"]`);
    
    // ajax_objectがグローバルに定義されていることを前提
    this.ajaxUrl = (window as any).ajax_object?.ajax_url || '';
    
    // バインドされた関数を保存
    this.boundHandleFileChange = this.handleFileChange.bind(this);
    this.boundHandleFileUploadButtonClick = this.handleFileUploadButtonClick.bind(this);
    this.boundHandleDelete = this.handleDelete.bind(this);
    
    this.init();
  }

  /**
   * デフォルト設定とマージ
   */
  protected mergeConfig(config: FileUploaderConfig): FileUploaderConfig {
    const defaultMessages = {
      uploading: 'アップロード中...',
      uploadSuccess: '✓ アップロード完了',
      deleting: '削除中...',
      deleteSuccess: '✓ ファイルを削除しました',
      deleteConfirm: 'アップロードしたファイルを削除しますか？',
      communicationError: '通信エラーが発生しました'
    };

    return {
      ...config,
      messages: {
        ...defaultMessages,
        ...config.messages
      }
    };
  }

  /**
   * 初期化処理
   */
  protected init(): void {
    this.bindAllEventListeners();
  }

  /**
   * 全てのイベントリスナーをバインド
   */
  protected bindAllEventListeners(): void {
    // 既存のリスナーを削除
    this.removeAllEventListeners();
    
    // 新しいリスナーを追加
    const fileInputs = this.container.querySelectorAll(`input[name="${this.config.fileInputName}"]`);
    fileInputs.forEach(input => {
      input.addEventListener('change', this.boundHandleFileChange);
    });
    
    this.bindFileUploadButtons();
    this.bindDeleteButtons();
  }

  /**
   * 全てのイベントリスナーを削除
   */
  protected removeAllEventListeners(): void {
    // ファイル入力のイベントリスナーを削除
    const fileInputs = this.container.querySelectorAll(`input[name="${this.config.fileInputName}"]`);
    fileInputs.forEach(input => {
      input.removeEventListener('change', this.boundHandleFileChange);
    });

    // ファイル選択ボタンのイベントリスナーを削除
    const uploadButtons = this.container.querySelectorAll('.js-file-upload-btn');
    uploadButtons.forEach(button => {
      button.removeEventListener('click', this.boundHandleFileUploadButtonClick);
    });

    // 削除ボタンのイベントリスナーを削除
    const deleteButtons = this.container.querySelectorAll('.js-remove-btn');
    deleteButtons.forEach(button => {
      button.removeEventListener('click', this.boundHandleDelete);
    });
  }

  /**
   * ファイル選択ボタンにイベントリスナーを設定
   */
  protected bindFileUploadButtons(): void {
    const uploadButtons = this.container.querySelectorAll('.js-file-upload-btn');
    uploadButtons.forEach(button => {
      button.addEventListener('click', this.boundHandleFileUploadButtonClick);
    });
  }

  /**
   * 削除ボタンにイベントリスナーを設定
   */
  protected bindDeleteButtons(): void {
    const deleteButtons = this.container.querySelectorAll('.js-remove-btn');
    deleteButtons.forEach(button => {
      button.addEventListener('click', this.boundHandleDelete);
    });
  }

  /**
   * ファイル選択ボタンクリック時の処理
   */
  protected handleFileUploadButtonClick(event: Event): void {
    event.preventDefault();
    
    // 隠しファイル入力を探してクリック（セレクタを修正）
    const fileInput = this.container.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  /**
   * ファイル選択時の処理
   */
  protected handleFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      console.log('New file selected:', file.name);
      console.log('File size:', file.size);
      console.log('File type:', file.type);
      
      this.setUploadState('uploading');
      this.uploadFile(file);
    }
  }

  /**
   * ファイルアップロード処理
   */
  protected async uploadFile(file: File): Promise<void> {
    const formData = new FormData();
    formData.append(this.config.fileInputName, file);
    formData.append('action', this.config.uploadAction);
    
    if (this.nonceField) {
      formData.append(this.config.nonceName, this.nonceField.value);
    }
    formData.append('file_changed', '1');
    
    try {
      const response = await fetch(this.ajaxUrl, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        this.updateFileDisplay(data.data.file_name);
        this.setUploadState('success');
        console.log('File upload successful:', data.data.file_name);
        
        // 成功時は3秒後に状態をクリア
        setTimeout(() => {
          this.clearUploadState();
        }, 3000);
      } else {
        this.setUploadState('error');
        console.error('File upload failed:', data.data.message);
        if (this.fileInput) {
          this.fileInput.value = '';
        }
      }
    } catch (error) {
      console.error('AJAX error:', error);
      this.setUploadState('error');
      if (this.fileInput) {
        this.fileInput.value = '';
      }
    }
  }

  /**
   * ファイル削除処理
   */
  protected handleDelete(): void {
    if (confirm(this.config.messages!.deleteConfirm!)) {
      console.log('File removal confirmed');
      this.setUploadState('deleting');
      this.deleteFile();
    }
  }

  /**
   * ファイル削除のAJAX処理
   */
  protected async deleteFile(): Promise<void> {
    const formData = new FormData();
    formData.append('action', this.config.deleteAction);
    
    if (this.nonceField) {
      formData.append(this.config.nonceName, this.nonceField.value);
    }
    
    try {
      const response = await fetch(this.ajaxUrl, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        this.resetFileDisplay();
        this.setUploadState('success');
        console.log('File delete successful:', data.data.message);
        
        // 成功時は3秒後に状態をクリア
        setTimeout(() => {
          this.clearUploadState();
        }, 3000);
      } else {
        this.setUploadState('error');
        console.error('File delete failed:', data.data.message);
      }
    } catch (error) {
      console.error('AJAX delete error:', error);
      this.setUploadState('error');
    }
  }

  /**
   * アップロード状態をクラスで設定
   */
  protected setUploadState(state: 'uploading' | 'deleting' | 'success' | 'error'): void {
    // 既存の状態クラスをクリア
    this.clearUploadState();
    
    // 新しい状態クラスを追加
    this.container.classList.add(`is-${state}`);
  }

  /**
   * アップロード状態をクリア
   */
  protected clearUploadState(): void {
    // 全ての状態クラスを削除
    this.container.classList.remove('is-uploading', 'is-deleting', 'is-success', 'is-error');
  }

  /**
   * ファイル表示を更新（サブクラスで実装）
   */
  protected abstract updateFileDisplay(fileName: string): void;

  /**
   * ファイル表示をリセット（サブクラスで実装）
   */
  protected abstract resetFileDisplay(): void;
}